import { css } from "@compiled/react";
import { format } from "date-fns";

import Avatar from "@atlaskit/avatar";
import EpicIcon from "@atlaskit/icon/core/epic";
import PriorityHighIcon from "@atlaskit/icon/core/priority-high";
import PriorityLowIcon from "@atlaskit/icon/core/priority-low";
import PriorityMediumIcon from "@atlaskit/icon/core/priority-medium";
import TaskIcon from "@atlaskit/icon/core/task";
import Lozenge from "@atlaskit/lozenge";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import {
    loadingBarStyles,
    tableBorderNotLastCellStyles,
    tableCellStyles
} from "./shared-styles.ts";

enum StatusDisplay {
    ToDo = "TO DO",
    InProgress = "IN PROGRESS",
    Complete = "COMPLETE"
}

enum PriorityDisplay {
    Low = "Low",
    Medium = "Medium",
    High = "High"
}

const inlineRowCss = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem"
});

const placeHolderBarStyles = {
    root: css(loadingBarStyles),
    narrow: css({
        width: "5rem"
    }),
    wide: css({
        width: "28.125rem"
    })
};

const lozengeStyles = {
    toDo: {
        backgroundColor: wacToken("color.neutral.light300"),
        color: wacToken("color.neutral.light1000")
    },
    inProgress: {
        backgroundColor: wacToken("color.extended.blue300"),
        color: wacToken("color.neutral.light1000")
    },
    complete: {
        backgroundColor: wacToken("color.extended.lime300"),
        color: wacToken("color.neutral.light1000")
    }
};

const dateCss = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    fontWeight: 600,
    fontSize: "0.7rem",
    lineHeight: "1rem",
    color: wacToken("color.neutral.light1000")
});

const avatarCss = css({
    display: "flex",
    alignItems: "center"
});

const epicSummaryCss = css({
    fontWeight: 700
});

const tableBorderNotLastRowCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderBlockEndColor: "rgba(11, 18, 40, 0.14)",
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "1px"
});

function renderStatusLozenge(status?: TableRowProps["status"]) {
    switch (status) {
        case "to do":
            return (
                <Lozenge style={lozengeStyles.toDo}>
                    {StatusDisplay.ToDo}
                </Lozenge>
            );
        case "in progress":
            return (
                <Lozenge style={lozengeStyles.inProgress}>
                    {StatusDisplay.InProgress}
                </Lozenge>
            );
        case "complete":
            return (
                <Lozenge style={lozengeStyles.complete}>
                    {StatusDisplay.Complete}
                </Lozenge>
            );
        default:
            return (
                <div
                    css={[
                        placeHolderBarStyles.root,
                        placeHolderBarStyles.narrow
                    ]}
                />
            );
    }
}

function renderDateLozenge(dueDate?: Date) {
    if (!dueDate)
        return (
            <div
                css={[placeHolderBarStyles.root, placeHolderBarStyles.narrow]}
            />
        );

    return <span css={dateCss}>{format(dueDate, "MMM do, yyyy")}</span>;
}

function renderPriority(priority?: TableRowProps["priority"]) {
    switch (priority) {
        case "low":
            return (
                <span css={inlineRowCss}>
                    <PriorityLowIcon
                        label="low priority"
                        color={token("color.icon.accent.blue")}
                    />
                    <span>{PriorityDisplay.Low}</span>
                </span>
            );
        case "medium":
            return (
                <span css={inlineRowCss}>
                    <PriorityMediumIcon
                        label="medium priority"
                        color={token("color.icon.accent.yellow")}
                    />
                    <span>{PriorityDisplay.Medium}</span>
                </span>
            );
        case "high":
            return (
                <span css={inlineRowCss}>
                    <PriorityHighIcon
                        label="high priority"
                        color={token("color.icon.accent.red")}
                    />
                    <span>{PriorityDisplay.High}</span>
                </span>
            );
        default:
            return (
                <div
                    css={[
                        placeHolderBarStyles.root,
                        placeHolderBarStyles.narrow
                    ]}
                />
            );
    }
}

function renderAssignee(assignee?: string, assigneeImgSrc?: string) {
    if (assignee && assigneeImgSrc) {
        return (
            <div css={[avatarCss, inlineRowCss]}>
                <Avatar
                    appearance="circle"
                    src={assigneeImgSrc}
                    size="small"
                    name={assignee}
                />
                <span>{assignee}</span>
            </div>
        );
    } else if (assignee) {
        return assignee;
    } else {
        return (
            <div
                css={[placeHolderBarStyles.root, placeHolderBarStyles.narrow]}
            />
        );
    }
}

export interface TableRowProps {
    type: "epic" | "task";
    summary: string;
    status: "to do" | "in progress" | "complete";
    dueDate: Date;
    priority: "low" | "medium" | "high";
    assignee: string;
    assigneeImgSrc?: string;
    isLastRow: boolean;
}

export const TableRow = ({
    type,
    summary,
    status,
    dueDate,
    priority,
    assignee,
    assigneeImgSrc,
    isLastRow
}: Partial<TableRowProps>) => {
    const summaryIcon =
        type === "epic" ? (
            <EpicIcon
                label=""
                size="small"
                color={token("color.icon.accent.purple")}
            />
        ) : (
            <TaskIcon
                label=""
                size="small"
                color={token("color.icon.accent.blue")}
            />
        );

    const summaryCell = summary || (
        <div css={[placeHolderBarStyles.root, placeHolderBarStyles.wide]} />
    );

    return (
        <tr>
            <td
                css={[
                    css(tableCellStyles),
                    type === "epic" && epicSummaryCss,
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowCss
                ]}
            >
                <span css={inlineRowCss}>
                    <span>{summaryIcon}</span>
                    <span>{summaryCell}</span>
                </span>
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowCss
                ]}
            >
                {renderStatusLozenge(status)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowCss
                ]}
            >
                {renderDateLozenge(dueDate)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    css(tableBorderNotLastCellStyles),
                    !isLastRow && tableBorderNotLastRowCss
                ]}
            >
                {renderPriority(priority)}
            </td>
            <td
                css={[
                    css(tableCellStyles),
                    !isLastRow && tableBorderNotLastRowCss
                ]}
            >
                {renderAssignee(assignee, assigneeImgSrc)}
            </td>
        </tr>
    );
};
